import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import StructuredData from '@/components/base/StructuredData';
import { aboutData } from '@/mocks/about';

const aboutStructuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: '欲望之岛',
      url: 'https://mindvoyage.github.io',
      description: '欲望之岛，一个让你直面内心真实渴望的沉浸式体验平台。',
      email: 'hello@yuwangzhidao.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '望京SOHO T3',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'hello@yuwangzhidao.com',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    {
      '@type': 'WebPage',
      name: '关于欲望之岛——探索内心深处的欲望世界',
      description: '了解欲望之岛的品牌故事、使命愿景和核心团队。我们致力于创造一个安全开放的自我探索空间。',
      url: 'https://mindvoyage.github.io/about',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mindvoyage.github.io' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <StructuredData data={aboutStructuredData} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="py-8 md:py-12 bg-background-50">
          <div className="container-custom max-w-4xl">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-100 mb-6">
              关于欲望之岛
            </h1>

            <div className="prose-custom text-foreground-400 leading-relaxed space-y-4 mb-14">
              {aboutData.introduction.map((paragraph, i) => (
                <p key={i} className="text-sm md:text-base">{paragraph}</p>
              ))}
            </div>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-semibold font-heading text-foreground-200 mb-8 text-center">
                我们的价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutData.values.map((value) => (
                  <div key={value.title} className="card-dark text-center group">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400 group-hover:bg-primary-500/20 transition-colors duration-300">
                      <i className={`${value.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-200 font-heading mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-semibold font-heading text-foreground-200 mb-8 text-center">
                核心团队
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutData.team.map((member) => (
                  <div key={member.name} className="card-dark text-center group">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                      <span className="text-2xl font-heading text-primary-400 font-bold">
                        {member.name.charAt(0)}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-200 font-label">
                      {member.name}
                    </h3>
                    <p className="text-xs text-primary-400 mb-2">{member.role}</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {member.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-semibold font-heading text-foreground-200 mb-8 text-center">
                探索者口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {aboutData.testimonials.map((t) => (
                  <div key={t.name} className="card-dark">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-full h-full object-cover"
                          title={`${t.name} - 欲望之岛探索者`}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-200 font-label">{t.name}</p>
                        <p className="text-xs text-foreground-600">{t.location}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-500 leading-relaxed italic">
                      "{t.text}"
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section id="contact" className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold font-heading text-foreground-200 mb-8 text-center">
                联系我们
              </h2>
              <div className="card-dark max-w-lg mx-auto">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm text-foreground-300">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信</p>
                      <p className="text-sm text-foreground-300">{aboutData.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm text-foreground-300">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">工作时间</p>
                      <p className="text-sm text-foreground-300">{aboutData.contact.hours}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}